import {
  type ReactNode,
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { CodeThemeProvider } from 'scoobie';
import { useLocalStorage } from '#src/hooks/storage.ts';

export type ColorMode = 'darkMode' | 'lightMode';

export type ColorModeContext = {
  colorMode: ColorMode | null;
  automaticColorMode: boolean;
  setAutomaticColorMode: (automatic: boolean) => void;
  setColorMode: (colorMode: ColorMode) => void;
};

const ctx = createContext<ColorModeContext>({
  colorMode: null,
  automaticColorMode: true,
  setAutomaticColorMode: () => {
    throw new Error('setAutomaticColorMode used outside of ColorModeProvider');
  },
  setColorMode: () => {
    throw new Error('setUserColorMode used outside of ColorModeProvider');
  },
});

interface ColorModeProviderProps {
  children: ReactNode;
}

export const ColorModeProvider = ({ children }: ColorModeProviderProps) => {
  const darkModeMediaQuery = useMemo(
    () => window.matchMedia('(prefers-color-scheme: dark)'),
    [],
  );
  const [colorModeLocalStorage, setColorModeLocalStorage] =
    useLocalStorage<ColorMode | null>('/user/color-mode', null);
  const [automaticColorModeLocalStorage, setAutomaticColorModeLocalStorage] =
    useLocalStorage<boolean>('/user/automatic-color-mode', true);

  const [colorMode, setColorMode] = useState<ColorMode | null>(null);
  const [automaticColorMode, setAutomaticColorMode] = useState<boolean>(false);

  useEffect(() => {
    if (colorModeLocalStorage) {
      setColorMode(colorModeLocalStorage);
    }
  }, [colorModeLocalStorage]);

  useEffect(() => {
    if (automaticColorModeLocalStorage) {
      setAutomaticColorMode(automaticColorModeLocalStorage);
    }
  }, [automaticColorModeLocalStorage]);

  const applyColorMode = useCallback(
    (event: MediaQueryList | MediaQueryListEvent) => {
      if (automaticColorMode) {
        setColorMode(event.matches ? 'darkMode' : 'lightMode');
      }
    },
    [automaticColorMode],
  );

  useEffect(() => {
    darkModeMediaQuery.addEventListener('change', applyColorMode);

    applyColorMode(darkModeMediaQuery);

    return () =>
      darkModeMediaQuery.removeEventListener('change', applyColorMode);
  }, [applyColorMode, darkModeMediaQuery]);

  const setAutomatic = (automatic: boolean) => {
    if (automatic) {
      setMode(darkModeMediaQuery.matches ? 'darkMode' : 'lightMode');
    }
    setAutomaticColorModeLocalStorage(automatic);
    setAutomaticColorMode(automatic);
  };

  const setMode = (mode: ColorMode) => {
    const darkModeClass =
      document.documentElement.getAttribute('data-dark-mode');

    if (darkModeClass) {
      if (mode === 'lightMode') {
        document.documentElement.classList.remove(darkModeClass);
      }

      if (mode === 'darkMode') {
        document.documentElement.classList.add(darkModeClass);
      }
    }
    setColorModeLocalStorage(mode);
    setColorMode(mode);
  };

  return (
    <ctx.Provider
      value={{
        colorMode,
        automaticColorMode,
        setAutomaticColorMode: setAutomatic,
        setColorMode: setMode,
      }}
    >
      <CodeThemeProvider
        theme={colorMode === 'darkMode' ? 'scoobieDark' : 'scoobieLight'}
      >
        {children}
      </CodeThemeProvider>
    </ctx.Provider>
  );
};

// Won't be set on initial render
export const useLazyColorMode = (): ColorModeContext | null => useContext(ctx);
